/* 0.48.86 - API answers kept for the visit.
 *
 * A call API effect with "Keep for this visit" stores its answer here,
 * and every page and flow of the site opened later in the same browser
 * tab starts with it: api("conn.request", "path") reads it at once and
 * a call that asks again with the same inputs as the page opens is not
 * made. "Always ask the API" still asks, and renews the kept answer.
 *
 * sessionStorage: the answer stays in this tab only and is gone when
 * the tab is closed. It belongs to the person signed in when it was
 * kept: AuthProvider calls setOwner, and a different person (or
 * nobody, after signing out) starts with nothing.
 */
const KEY = 'gt18.visit_api';
const MAX_ENTRY = 200000;      // characters of one answer
const MAX_ENTRIES = 30;

function read() {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
    return v && typeof v === 'object' && v.answers && typeof v.answers === 'object'
      ? v : { owner: null, answers: {} };
  } catch {
    return { owner: null, answers: {} };
  }
}

function write(v) {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(v));
  } catch { /* full or blocked: the page works without it */ }
}

/* The answers as api state entries: { 'conn.request': {status, response, inputs, base} }. */
export function loadVisitApi() {
  const out = {};
  for (const [slug, e] of Object.entries(read().answers)) {
    if (e && e.response !== undefined) {
      out[slug] = { status: 'ok', response: e.response, inputs: e.inputs || {}, base: e.base || {}, kept: true };
    }
  }
  return out;
}

export function isKeptForVisit(slug) {
  return !!read().answers[slug];
}

export function keepForVisit(slug, response, inputs, base) {
  let text;
  try { text = JSON.stringify(response); } catch { return; }
  if (!text || text.length > MAX_ENTRY) return;
  const v = read();
  delete v.answers[slug];
  v.answers[slug] = { response, inputs: inputs || {}, base: base || {}, at: Date.now() };
  const keys = Object.keys(v.answers);
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete v.answers[k];
  write(v);
}

/* The signed-in person's id, or null. A change of person clears the
   answers, so nobody sees what was kept for someone else. */
export function setVisitOwner(id) {
  const owner = id == null ? null : String(id);
  const v = read();
  if (v.owner !== owner) write({ owner, answers: {} });
}

/* Whose answers these are: the id given to setVisitOwner, or null. */
export function visitOwner() {
  return read().owner;
}

export function clearVisitApi() {
  try { window.sessionStorage.removeItem(KEY); } catch { /* nothing kept */ }
}
